import { apiClient } from './apiClient';

import type { UserRole } from '@little-chef/domain';

export interface VerifyTokenResponse {
  user: {
    id: string;
    email: string;
    role: UserRole;
    userProviderId: string;
    username: string | null;
    termsAcceptedAt: string | null;
    mailingConsent: boolean;
    weekStartDay: number;
  };
}

interface ApiResponse<T> {
  data: T;
  error: null;
}

/**
 * Fetch a CSRF token from the server.
 * This sets the `__csrf` non-HttpOnly cookie used by subsequent mutation requests.
 * Must be called once at app startup before any POST/PUT/PATCH/DELETE.
 */
export async function fetchCsrfToken(): Promise<void> {
  await apiClient.get('/auth/csrf-token');
}

/**
 * Verify Firebase ID token with backend.
 * The backend creates a session cookie and returns the user payload.
 */
export async function verifyToken(
  idToken: string,
  consent?: { termsAccepted?: boolean; mailingConsent?: boolean },
): Promise<VerifyTokenResponse> {
  try {
    await fetchCsrfToken();

    const response = await apiClient.post<ApiResponse<VerifyTokenResponse>>('/auth/verify', {
      idToken,
      ...consent,
    });

    const payload = response.data?.data;

    if (!payload) {
      throw new Error('Invalid verify response: missing data payload');
    }

    return payload;
  } catch (error) {
    throw error;
  }
}

/**
 * Fetch the currently authenticated user by verifying the __session cookie.
 * Returns the user if the session is valid, throws on 401.
 */
export async function getMe(): Promise<VerifyTokenResponse> {
  const response = await apiClient.get<ApiResponse<VerifyTokenResponse>>('/auth/me');
  const payload = response.data?.data;
  if (!payload) throw new Error('Invalid /auth/me response: missing data payload');
  return payload;
}

/**
 * Logout — clears session and CSRF cookies server-side.
 */
export async function logout(): Promise<void> {
  await apiClient.post('/auth/logout', {});
}
